import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useProjects } from './ProjectContext';
import contextService from '../services/contextService';
import primerService, { hashSignals, hashSources } from '../services/primerService';
import { computeReadiness } from '../utils/readiness';
import {
  ContextSource,
  Signal,
  SignalCategory,
  ReadinessBreakdown,
  LiveSuggestion,
} from '../types/context.types';

interface ContextEngineContextType {
  // Data
  sources: ContextSource[];
  signals: Signal[];
  readinessScore: number;
  readinessBreakdown: ReadinessBreakdown;
  liveSuggestions: LiveSuggestion[];
  hasCompletedOnboarding: boolean;

  // Loading states
  loadingSources: boolean;
  loadingSignals: boolean;
  loadingSuggestions: boolean;
  processingContext: boolean;

  // Dragging state
  draggingSignal: Signal | null;
  setDraggingSignal: (signal: Signal | null) => void;

  // Click-to-insert handler (registered by page components)
  onSignalClick: ((signal: Signal) => void) | null;
  registerSignalClickHandler: (handler: ((signal: Signal) => void) | null) => void;

  // Source operations
  uploadSource: (
    projectId: string,
    fileName: string,
    fileType: string,
    fileSize: number,
    data: string
  ) => Promise<{ source: ContextSource; signals: Signal[] }>;
  deleteSource: (sourceId: string) => Promise<void>;
  refreshSources: () => Promise<void>;

  // Signal operations
  createManualSignal: (projectId: string, label: string, category?: SignalCategory) => Promise<Signal>;
  updateSignal: (
    signalId: string,
    updates: { label?: string; category?: SignalCategory; description?: string }
  ) => Promise<Signal>;
  deleteSignal: (signalId: string) => Promise<void>;
  toggleSignal: (signalId: string) => Promise<Signal>;
  refreshSignals: () => Promise<void>;

  // Readiness & suggestions
  refreshReadiness: () => Promise<void>;
  refreshSuggestions: () => Promise<void>;

  // Onboarding
  processOnboarding: (
    projectId: string,
    files: Array<{ fileName: string; fileType: string; fileSize: number; data: string }>,
    manualTags?: Array<{ label: string; category: SignalCategory }>
  ) => Promise<{
    sources: ContextSource[];
    signals: Signal[];
    readiness: { score: number; breakdown: ReadinessBreakdown; suggestions: LiveSuggestion[] };
  }>;
  markOnboardingComplete: () => Promise<void>;
}

const ContextEngineContext = createContext<ContextEngineContextType | undefined>(undefined);

export function useContextEngine() {
  const context = useContext(ContextEngineContext);
  if (context === undefined) {
    throw new Error('useContextEngine must be used within a ContextEngineProvider');
  }
  return context;
}

interface ContextEngineProviderProps {
  children: React.ReactNode;
}

const EMPTY_BREAKDOWN: ReadinessBreakdown = {
  dataSourcesScore: 0,
  sourceCount: 0,
  hasMultipleSources: false,
  hasProcessedSources: false,
  manualTagsScore: 0,
  manualTagCount: 0,
  hasGoalTag: false,
  hasAudienceTag: false,
  hasToneTag: false,
  hasConstraintTag: false,
  interactionsScore: 0,
  conversationCount: 0,
  chatExtractedSignalCount: 0,
};

export function ContextEngineProvider({ children }: ContextEngineProviderProps) {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const { currentProject } = useProjects();

  const [sources, setSources] = useState<ContextSource[]>([]);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [readinessScore, setReadinessScore] = useState(0);
  const [readinessBreakdown, setReadinessBreakdown] = useState<ReadinessBreakdown | null>(null);
  const [liveSuggestions, setLiveSuggestions] = useState<LiveSuggestion[]>([]);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  const [loadingSources, setLoadingSources] = useState(false);
  const [loadingSignals, setLoadingSignals] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [processingContext, setProcessingContext] = useState(false);

  const [draggingSignal, setDraggingSignal] = useState<Signal | null>(null);
  const [onSignalClick, setOnSignalClick] = useState<((signal: Signal) => void) | null>(null);

  const registerSignalClickHandler = useCallback(
    (handler: ((signal: Signal) => void) | null) => {
      setOnSignalClick(() => handler);
    },
    []
  );

  // Track onboarding status from userProfile
  useEffect(() => {
    if (userProfile) {
      setHasCompletedOnboarding(!!userProfile.onboardingCompleted);
    }
  }, [userProfile]);

  const projectId = currentProject?.id;

  // Hydrate signals/sources from localStorage on projectId change. Reset state
  // synchronously so the write-cache effects below cannot leak the previous
  // project's data into the new project's keys during the render where
  // projectId has flipped but state hasn't yet.
  useEffect(() => {
    if (projectId) {
      let nextSignals: Signal[] = [];
      let nextSources: ContextSource[] = [];
      try {
        const cachedSig = localStorage.getItem(`signals_${projectId}`);
        if (cachedSig) nextSignals = JSON.parse(cachedSig);
      } catch {
        // ignore parse errors
      }
      try {
        const cachedSrc = localStorage.getItem(`sources_${projectId}`);
        if (cachedSrc) nextSources = JSON.parse(cachedSrc);
      } catch {
        // ignore parse errors
      }
      setSignals(nextSignals);
      setSources(nextSources);
      setReadinessScore(0);
      setReadinessBreakdown(null);
      setLiveSuggestions([]);
    } else {
      setSources([]);
      setSignals([]);
      setReadinessScore(0);
      setReadinessBreakdown(null);
      setLiveSuggestions([]);
    }
  }, [projectId]);

  // Cache signals in localStorage when they change
  useEffect(() => {
    if (projectId && signals.length > 0) {
      try {
        localStorage.setItem(`signals_${projectId}`, JSON.stringify(signals));
      } catch {
        // ignore storage errors
      }
    }
  }, [projectId, signals]);

  // Cache sources in localStorage so the primer's sources hash matches across
  // mounts — without this, the post-onboarding primer cache always misses on
  // return because hashSources([]) !== hashSources(originalSources).
  useEffect(() => {
    if (projectId && sources.length > 0) {
      try {
        localStorage.setItem(`sources_${projectId}`, JSON.stringify(sources));
      } catch {
        // ignore storage errors
      }
    }
  }, [projectId, sources]);

  const refreshSources = useCallback(async () => {
    if (!projectId || !currentUser) return;
    setLoadingSources(true);
    try {
      const fetched = await contextService.getSources(projectId);
      setSources(fetched);
    } catch (err) {
      console.error('Error fetching sources:', err);
    } finally {
      setLoadingSources(false);
    }
  }, [projectId, currentUser]);

  const refreshSignals = useCallback(async () => {
    if (!projectId || !currentUser) return;
    setLoadingSignals(true);
    try {
      const fetched = await contextService.getSignals(projectId);
      setSignals(fetched);
    } catch (err) {
      console.error('Error fetching signals:', err);
    } finally {
      setLoadingSignals(false);
    }
  }, [projectId, currentUser]);

  const refreshReadiness = useCallback(async () => {
    if (!projectId || !currentUser) return;
    try {
      const result = await contextService.getReadiness(projectId);
      setReadinessScore(result.score);
      setReadinessBreakdown(result.breakdown);
    } catch (err) {
      console.error('Error fetching readiness:', err);
    }
  }, [projectId, currentUser]);

  // Recompute the readiness score locally from the post-mutation sources/signals
  // so the meter updates in the same render as the sidebar list, without waiting
  // for a second network round-trip to /api/context/readiness. Interactions are
  // unaffected by signal/source mutations, so we preserve the server's values.
  const applyOptimisticReadiness = useCallback(
    (nextSources: ContextSource[], nextSignals: Signal[]) => {
      const { score, breakdown } = computeReadiness(nextSources, nextSignals, {
        conversationCount: readinessBreakdown?.conversationCount ?? 0,
        score: readinessBreakdown?.interactionsScore ?? 0,
      });
      setReadinessScore(score);
      setReadinessBreakdown(breakdown);
    },
    [readinessBreakdown]
  );

  const refreshSuggestions = useCallback(async () => {
    if (!projectId || !currentUser) return;
    setLoadingSuggestions(true);
    try {
      const fetched = await contextService.getSuggestions(projectId);
      setLiveSuggestions(fetched);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [projectId, currentUser]);

  // Auto-fetch when project changes
  useEffect(() => {
    if (projectId && currentUser) {
      refreshSignals();
      refreshReadiness();
      refreshSources();
      refreshSuggestions();
    }
  }, [projectId, currentUser, refreshSignals, refreshReadiness, refreshSources, refreshSuggestions]);

  // Poll while any source is still processing. Chat-uploaded files are processed
  // by a fire-and-forget backend job, so the row appears as 'uploading'/'processing'
  // and resolves to 'ready' minutes later. Polling keeps the sidebar accurate
  // without a manual refresh. Stops once all sources reach a terminal status.
  // pollCountRef is held across effect re-runs to enforce a hard cap; it resets
  // whenever the project changes or all sources reach a terminal state.
  const pollCountRef = useRef(0);
  useEffect(() => {
    if (!projectId || !currentUser) return;
    const hasInFlight = sources.some(
      (s) => s.status === 'uploading' || s.status === 'processing'
    );
    if (!hasInFlight) {
      pollCountRef.current = 0;
      return;
    }
    if (pollCountRef.current >= 30) return;

    const interval = setInterval(() => {
      pollCountRef.current += 1;
      refreshSources();
      refreshSignals();
      refreshReadiness();
    }, 4000);
    return () => clearInterval(interval);
  }, [projectId, currentUser, sources, refreshSources, refreshSignals, refreshReadiness]);

  // Reset poll counter when project changes
  useEffect(() => {
    pollCountRef.current = 0;
  }, [projectId]);

  const uploadSource = async (
    pid: string,
    fileName: string,
    fileType: string,
    fileSize: number,
    data: string
  ) => {
    const result = await contextService.uploadSource(pid, fileName, fileType, fileSize, data);
    const nextSources = [result.source, ...sources];
    const nextSignals =
      result.signals.length > 0 ? [...result.signals, ...signals] : signals;
    setSources(nextSources);
    if (result.signals.length > 0) {
      setSignals(nextSignals);
    }
    applyOptimisticReadiness(nextSources, nextSignals);
    refreshReadiness();
    refreshSuggestions();
    return result;
  };

  const deleteSource = async (sourceId: string) => {
    await contextService.deleteSource(sourceId);
    const nextSources = sources.filter((s) => s.id !== sourceId);
    // Also drop signals linked exclusively to this source; the server cascade
    // is authoritative, but this keeps the optimistic meter closer to truth.
    const nextSignals = signals.filter(
      (s) => !(s.sourceIds.length === 1 && s.sourceIds[0] === sourceId)
    );
    setSources(nextSources);
    if (nextSignals.length !== signals.length) {
      setSignals(nextSignals);
    }
    applyOptimisticReadiness(nextSources, nextSignals);
    refreshSignals();
    refreshReadiness();
    refreshSuggestions();
  };

  const createManualSignal = async (pid: string, label: string, category?: SignalCategory) => {
    const signal = await contextService.createManualSignal(pid, label, category);
    const nextSignals = [signal, ...signals];
    setSignals(nextSignals);
    applyOptimisticReadiness(sources, nextSignals);
    refreshReadiness();
    refreshSuggestions();
    return signal;
  };

  const updateSignal = async (
    signalId: string,
    updates: { label?: string; category?: SignalCategory; description?: string }
  ) => {
    const updated = await contextService.updateSignal(signalId, updates);
    const nextSignals = signals.map((s) => (s.id === signalId ? updated : s));
    setSignals(nextSignals);
    applyOptimisticReadiness(sources, nextSignals);
    refreshReadiness();
    refreshSuggestions();
    return updated;
  };

  const deleteSignal = async (signalId: string) => {
    await contextService.deleteSignal(signalId);
    const nextSignals = signals.filter((s) => s.id !== signalId);
    setSignals(nextSignals);
    applyOptimisticReadiness(sources, nextSignals);
    refreshReadiness();
    refreshSuggestions();
  };

  const toggleSignal = async (signalId: string) => {
    const updated = await contextService.toggleSignal(signalId);
    const nextSignals = signals.map((s) => (s.id === signalId ? updated : s));
    setSignals(nextSignals);
    applyOptimisticReadiness(sources, nextSignals);
    refreshReadiness();
    refreshSuggestions();
    return updated;
  };

  const processOnboarding = async (
    pid: string,
    files: Array<{ fileName: string; fileType: string; fileSize: number; data: string }>,
    manualTags?: Array<{ label: string; category: SignalCategory }>
  ) => {
    setProcessingContext(true);
    try {
      const result = await contextService.processOnboarding(pid, files, manualTags);
      setSources(result.sources);
      setSignals(result.signals);
      setReadinessScore(result.readiness.score);
      setReadinessBreakdown(result.readiness.breakdown);
      setLiveSuggestions(result.readiness.suggestions);

      // Seed the primer localStorage cache with the same hash key Home computes,
      // so the post-onboarding /home render hits the cache instead of waiting on
      // a Claude call. Falls back to a normal fetch if the backend omitted it.
      if (result.primer) {
        primerService.setCache(
          pid,
          hashSignals(result.signals),
          hashSources(result.sources),
          result.primer
        );
      }

      return result;
    } finally {
      setProcessingContext(false);
    }
  };

  const markOnboardingComplete = async () => {
    await contextService.markOnboardingComplete();
    // Refresh AuthContext's userProfile so PrivateRoute sees onboardingCompleted: true
    // This must happen BEFORE setting local state to avoid a redirect loop
    // between PrivateRoute (checks userProfile) and Onboarding (checks hasCompletedOnboarding)
    try {
      await refreshUserProfile();
    } catch {
      // Non-critical: worst case PrivateRoute redirects once more
    }
    setHasCompletedOnboarding(true);
  };

  const value: ContextEngineContextType = {
    sources,
    signals,
    readinessScore,
    readinessBreakdown: readinessBreakdown || EMPTY_BREAKDOWN,
    liveSuggestions,
    hasCompletedOnboarding,
    loadingSources,
    loadingSignals,
    loadingSuggestions,
    processingContext,
    draggingSignal,
    setDraggingSignal,
    onSignalClick,
    registerSignalClickHandler,
    uploadSource,
    deleteSource,
    refreshSources,
    createManualSignal,
    updateSignal,
    deleteSignal,
    toggleSignal,
    refreshSignals,
    refreshReadiness,
    refreshSuggestions,
    processOnboarding,
    markOnboardingComplete,
  };

  return (
    <ContextEngineContext.Provider value={value}>
      {children}
    </ContextEngineContext.Provider>
  );
}
